import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import isScenarioDisabled from 'views/bluviu-nutrition/features/scenarios/utils/validate-scenario';

import { Box, Button, CircularProgress } from '@mui/material';
import Collapsible from 'components/collapsible';
import Divider from 'components/divider';
import LoadingSpinner from 'components/loading-spinner';
import { useAccountState } from 'contexts/account/account-context';
import dayjs from 'dayjs';
import { TimerReset } from 'lucide-react';
import Mui from 'materials';
import { useQueryClient } from '@tanstack/react-query';
import ScenarioInformation from 'views/bluviu-nutrition/features/scenarios/components/edit-scenario/scenario-information';
import ScenarioInputs from 'views/bluviu-nutrition/features/scenarios/components/edit-scenario/scenario-inputs';
import { useScenarioState } from 'views/bluviu-nutrition/features/scenarios/context/scenario-context';
import { UpdateScenarioRequest } from 'views/bluviu-nutrition/features/scenarios/types/update-scenario-input.type';
import { useUpdateScenario } from '../../hooks/use-update-scenario';
import { ScenariosParams } from '../../types/scenarios-params.type';

type Props = {
  isLoading: boolean;
};

const PAGE_NAME_ID = 'bluViuNutrition.setUp';

const SetupSite: React.FC<Props> = ({ isLoading }) => {
  const intl = useIntl();
  const params = useParams<ScenariosParams>();
  const queryClient = useQueryClient();
  const { updateScenario } = useUpdateScenario();
  const {
    account: { company },
  } = useAccountState();

  const {
    scenarioQuery: { data: scenario, isLoading: isScenarioLoading },
  } = useScenarioState();

  // FORM

  const defaultValues: UpdateScenarioRequest = useMemo(() => {
    return {
      name: scenario?.name ?? '',
      description: scenario?.description ?? '',
      startDate: scenario?.startDate ?? '',
      endDate: scenario?.endDate ?? '',
      fishWeight: String(scenario?.fishWeight) ?? '',
      calibrationId: scenario?.calibrationId ?? '',
    };
  }, [scenario]);

  const {
    handleSubmit,
    control,
    getValues,
    trigger,
    reset,
    formState: { isDirty, isValid },
  } = useForm<UpdateScenarioRequest>({
    defaultValues: defaultValues,
    shouldUnregister: true,
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [scenario]);
  // POST DATA

  // HANDLERS
  const handleUpdateScenario = (formData: UpdateScenarioRequest) => {
    updateScenario.mutate(
      {
        scenarioId: params.scenarioId,
        request: {
          name: formData.name,
          description: formData.description,
          startDate: dayjs(formData.startDate).utc(true).toISOString(),
          endDate: dayjs(formData.endDate).utc(true).toISOString(),
          fishWeight: formData.fishWeight,
          calibrationId: formData.calibrationId,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['getScenario', company!.companyId, params.scenarioId]
          });
        },
      }
    );
  };

  //#endregion HELPERS

  if (isScenarioLoading) return <LoadingSpinner />;
  if (!scenario) return;

  return (
    <Collapsible label={intl.formatMessage({ id: `${PAGE_NAME_ID}.title` })} hideBorder open={true}>
      <form onSubmit={handleSubmit(handleUpdateScenario)}>
        <Box display={'flex'} flexDirection={'column'} gap={6}>
          <ScenarioInformation control={control} isLoading={isLoading} />
          <Divider dark />
          <ScenarioInputs control={control} isLoading={isLoading} getValues={getValues} trigger={trigger} />
          <Box display={'flex'} justifyContent={'space-between'}>
            <Box display={'flex'} alignItems={'center'}>
              <Mui.Tooltip title={'Reset inputs'}>
                <Mui.IconButton disabled={!isDirty} onClick={() => reset(defaultValues)}>
                  <TimerReset size={22} />
                </Mui.IconButton>
              </Mui.Tooltip>
            </Box>
            <Button
              type="submit"
              size="medium"
              disabled={updateScenario.isPending || !isDirty || !isValid}
              startIcon={isLoading || updateScenario.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              <FormattedMessage id={`${PAGE_NAME_ID}.save`} />
            </Button>
          </Box>
        </Box>
      </form>
    </Collapsible>
  );
};

export default React.memo(SetupSite);
